import { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, clearUserConversation } from '../services/api';
import CustomDropdown from './CustomDropdown';

function Users() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    role: 'employee',
    whatsappNumber: '',
    whatsappEnabled: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      alert('Error loading users: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
      } else {
        await createUser(formData);
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', name: '', email: '', role: 'employee', whatsappNumber: '', whatsappEnabled: false });
      loadUsers();
    } catch (error) {
      alert('Error saving user: ' + error.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't populate password for security
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'employee',
      whatsappNumber: user.whatsappNumber || '',
      whatsappEnabled: user.whatsappEnabled || false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await deleteUser(id);
        loadUsers();
      } catch (error) {
        alert('Error deleting user: ' + error.message);
      }
    }
  };

  const handleReactivate = async (user) => {
    if (window.confirm(`Reactivate user ${user.username}? They will be able to log in and access the system again.`)) {
      try {
        await updateUser(user.id, { isActive: true });
        loadUsers();
      } catch (error) {
        alert('Error reactivating user: ' + error.message);
      }
    }
  };

  const handleClearConversation = async (user) => {
    if (window.confirm(`Clear all conversation history for ${user.username}? This will reset the AI's memory of conversations with this user.`)) {
      try {
        const response = await clearUserConversation(user.id);
        alert(response.message || 'Conversation cleared successfully');
      } catch (error) {
        alert('Error clearing conversation: ' + error.message);
      }
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: '#C8342B',
      employee: '#3D1F1F'
    };

    return (
      <span style={{
        padding: '4px 12px',
        background: colors[role] || '#95a5a6',
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {role.toUpperCase()}
      </span>
    );
  };

  const getFilteredUsers = () => {
    if (!searchTerm) {
      return users;
    }

    return users.filter(user => {
      const username = (user.username || '').toLowerCase();
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const whatsappNumber = (user.whatsappNumber || '').toLowerCase();
      const role = (user.role || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      return username.includes(searchLower) ||
             name.includes(searchLower) ||
             email.includes(searchLower) ||
             whatsappNumber.includes(searchLower) ||
             role.includes(searchLower);
    });
  };

  const filteredUsers = getFilteredUsers();

  const inputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };

  const buttonStyle = {
    padding: '10px 20px',
    background: '#C8342B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    marginRight: '10px',
    fontWeight: '500'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#3D1F1F' }}>User Management</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingUser(null);
            setFormData({ username: '', password: '', name: '', email: '', role: 'employee', whatsappNumber: '', whatsappEnabled: false });
          }}
          style={buttonStyle}
        >
          Add User
        </button>
      </div>

      {/* Search Bar */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <label style={{
          display: 'block',
          marginBottom: '5px',
          fontSize: '12px',
          fontWeight: '600',
          color: '#7f8c8d'
        }}>
          Search
        </label>
        <input
          type="text"
          placeholder="Search by username, name, email, WhatsApp number, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* User Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2>
              {editingUser ? 'Edit User' : 'Add New User'}
              {editingUser?.role === 'admin' && (
                <span style={{ fontSize: '14px', color: '#C8342B', fontWeight: 'normal', marginLeft: '10px' }}>
                  (Administrator - Limited Editing)
                </span>
              )}
            </h2>
            {editingUser?.role === 'admin' && (
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '10px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#856404'
              }}>
                ⚠️ Admin Protection: You can update <strong>Name, Email, and WhatsApp Number</strong>.
                Password and Role cannot be changed for security.
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <input
                style={inputStyle}
                type="text"
                placeholder="Username *"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <input
                  style={{...inputStyle, paddingRight: '40px', marginBottom: 0}}
                  type={showPassword ? "text" : "password"}
                  placeholder={editingUser ? "Password (leave blank to keep current)" : "Password *"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  disabled={editingUser?.role === 'admin'}
                  title={editingUser?.role === 'admin' ? 'Admin password cannot be changed' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    color: '#7f8c8d',
                    fontSize: '16px'
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <input
                style={inputStyle}
                type="text"
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <input
                style={inputStyle}
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />

              {/* WhatsApp Fields */}
              <div style={{ marginTop: '15px', marginBottom: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#3D1F1F', fontWeight: '600' }}>WhatsApp Access</h4>
                <input
                  style={inputStyle}
                  type="tel"
                  placeholder="WhatsApp Number (with country code, e.g., +919876543210)"
                  value={formData.whatsappNumber}
                  onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
                />
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#2c3e50', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.whatsappEnabled}
                    onChange={(e) => setFormData({ ...formData, whatsappEnabled: e.target.checked })}
                    style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span>Enable WhatsApp chatbot access for this user</span>
                </label>
                <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                  User must provide WhatsApp number to use chatbot features
                </p>
              </div>

              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d' }}>
                Role
              </label>
              <CustomDropdown
                value={formData.role}
                onChange={(value) => setFormData({ ...formData, role: value })}
                options={[
                  { value: 'employee', label: 'Employee' }
                ]}
                placeholder="Select role"
                style={{ marginBottom: '10px' }}
                disabled={editingUser?.role === 'admin'}
              />

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" style={buttonStyle}>
                  {editingUser ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingUser(null);
                  }}
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid #f0f0f0'
      }}>
        {users.length === 0 ? (
          <p style={{ color: '#7f8c8d' }}>No users found. Add your first user!</p>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Username</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>WhatsApp</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Role</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{user.username}</td>
                    <td style={{ padding: '12px' }}>{user.name || '-'}</td>
                    <td style={{ padding: '12px' }}>{user.email || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {user.whatsappEnabled ? (
                        <div>
                          <span style={{
                            padding: '3px 8px',
                            background: '#d4edda',
                            color: '#155724',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            marginRight: '5px'
                          }}>
                            ✓ Enabled
                          </span>
                          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                            {user.whatsappNumber || 'No number'}
                          </div>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#95a5a6' }}>Disabled</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>{getRoleBadge(user.role)}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: user.isActive ? '#d4edda' : '#f8d7da',
                        color: user.isActive ? '#155724' : '#721c24',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#7f8c8d' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEdit(user)}
                          style={{ ...buttonStyle, padding: '5px 10px', fontSize: '12px', margin: 0 }}
                        >
                          Edit
                        </button>
                        {user.whatsappEnabled && user.whatsappNumber && (
                          <button
                            onClick={() => handleClearConversation(user)}
                            style={{ ...buttonStyle, padding: '5px 10px', fontSize: '12px', margin: 0, background: '#f39c12' }}
                            title="Clear AI conversation history for this user"
                          >
                            Clear Chat
                          </button>
                        )}
                        {user.role !== 'admin' && user.isActive && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            style={{ ...buttonStyle, padding: '5px 10px', fontSize: '12px', margin: 0, background: '#e74c3c' }}
                          >
                            Deactivate
                          </button>
                        )}
                        {user.role !== 'admin' && !user.isActive && (
                          <button
                            onClick={() => handleReactivate(user)}
                            style={{ ...buttonStyle, padding: '5px 10px', fontSize: '12px', margin: 0, background: '#27ae60' }}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;
